import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { SetLocalTimeZone } from "@bbwt/utils";

import {
    IHttpResponsesHandler,
    IHttpResponseHandlerSettings,
    HttpResponsesHandlersFactory,
} from "@bbwt/modules/data-service";

import { IPagedData, PagedCrudService } from "@features/grid";

import {
    IQuerySchema,
    IQuerySource,
    IQueryPageRequest,
    IQuerySourcePreload, IOwnerUser
} from "../core/reporting-models";
import { QueryData } from "../core/query-hub/models";
import { PublishInfo } from "@main/reporting.v3/publishing/publish-info";
import { IPublishService } from "@main/reporting.v3/publishing/publish-service";


@Injectable()
export class QuerySourceService extends PagedCrudService<IQuerySource> implements IPublishService {
    readonly url: string = "api/reporting3/query-source";
    readonly entityTitle: string = "Query Source";


    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }


    getQuerySchema(querySourceId: string): Promise<IQuerySchema> {
        return this.handle(this.http.get<IQuerySchema>(`${this.url}/${querySourceId}/query-schema`));
    }

    getQuerySourcePreload(queryName: string, settings?: IHttpResponseHandlerSettings): Promise<IQuerySourcePreload> {
        const encodedName: string = encodeURIComponent(queryName);
        const handler: IHttpResponsesHandler = this.handlersFactory.getDefault(settings);

        return this.httpGet<IQuerySourcePreload>(`${encodedName}/preload`, handler);
    }

    getQueryData(querySourceId: string, request: IQueryPageRequest, settings?: IHttpResponseHandlerSettings): Promise<QueryData> {
        return this.handle(this.http.post<QueryData>(`${this.url}/${querySourceId}/query-full-schema`, request),
            this.handlersFactory.getDefault(settings));
    }

    async getQueryPage(querySourceId: string, queryPageRequest: IQueryPageRequest,
                       responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IPagedData<any>> {
        const result = await this.httpPost<IPagedData<any[]>>(`${querySourceId}/page`,
            queryPageRequest, this.handlersFactory.getForReadByFilter(responseHandlerSettings));

        result.items.forEach(item => this.modelUtcDateFields
            .forEach(utcField => (item[utcField] = SetLocalTimeZone(item[utcField]))));

        return result;
    }

    hasAttachedWidgets(querySourceId: string, disableAuthorization: boolean = false): Promise<boolean> {
        return this.handle(this.http.get<boolean>(`${this.url}/${querySourceId}/has-attached-widgets`,
            { params: this.constructHttpParams({ disableAuthorization: disableAuthorization }) }));
    }

    savePublishInfo(entityId: string, publishInfo: PublishInfo, settings?: IHttpResponseHandlerSettings): Promise<void> {
        const handler: IHttpResponsesHandler = this.handlersFactory.getForUpdate(this.entityTitle, settings);

        return this.httpPut<void>(`${entityId}/publish`, publishInfo, handler);
    }

    getUsersAllowedAsOwners(): Promise<IOwnerUser[]> {
        return this.handle(this.http.get<IOwnerUser[]>(`${this.url}/users-allowed-as-owners`));
    }

    changeOwner(querySourceId: string, ownerUserId: string): Promise<void> {
        return this.httpPut(`${querySourceId}/${ownerUserId}/change-owner`, null,
            this.handlersFactory.getForUpdate(this.entityTitle));
    }

    getPublishInfo(entityId: string, settings?: IHttpResponseHandlerSettings): Promise<PublishInfo> {
        const handler: IHttpResponsesHandler = this.handlersFactory.getForReadById(this.entityTitle, settings);

        return this.httpGet<PublishInfo>(`${entityId}/publish`, handler);
    }
}
